import { createSelector } from 'reselect';
import { makeGatedSelector } from './helpers';
import { getAuth, getCanUseGridLayoutGate, getIsUngatedForImmerModuleReducer } from './authSelectors';
import { List, Map as ImmutableMap, fromJS } from 'immutable';
import { getAllModuleSchemasByModuleId, getModuleSchemas } from 'ContentEditorUI/redux/selectors/moduleSchemaSelectors';
import { createModuleListsSelector, getSchemaWidgets } from './moduleSelectorHelpers';
import createMockTypedImmutableMap from '../../utils/createMockTypedImmutableMap';
// @ts-ignore not typed
import { getAllFieldNamesOnModuleSpec } from '../../data/moduleUtils';
import { convertFieldValueAvroWithoutTypenameToJson } from 'layout-data-lib/GridLayoutDataTree/typenameUtils';
import { ATTRIBUTES_PRIMARILY_FROM_CONTENT_WIDGET_WITH_EMPTY_SCHEMA_WIDGET } from '../../data/moduleTransformHelpers';
import mergeIn from '../../lib/mergeIn';
import cloneDeep from '../../utils/cloneDeep';
export const _getContentGridLayoutTrees = createModuleListsSelector('gridLayoutTrees');
export const getContentGridLayoutTrees = makeGatedSelector(getIsUngatedForImmerModuleReducer, createSelector([getCanUseGridLayoutGate, _getContentGridLayoutTrees.gated], (canUseGridLayoutGate, contentGridLayoutTrees) => canUseGridLayoutGate ? contentGridLayoutTrees : ImmutableMap()), createSelector([getCanUseGridLayoutGate, _getContentGridLayoutTrees.ungated], (canUseGridLayoutGate, contentGridLayoutTrees) => canUseGridLayoutGate ? contentGridLayoutTrees : {}));
export const _getGridLayoutModules = createModuleListsSelector('gridLayoutModules');
export const getGridLayoutModules = makeGatedSelector(getIsUngatedForImmerModuleReducer, createSelector([getCanUseGridLayoutGate, _getGridLayoutModules.gated], (canUseGridLayoutGate, gridLayoutModules) => canUseGridLayoutGate ? gridLayoutModules : ImmutableMap()), createSelector([getCanUseGridLayoutGate, _getGridLayoutModules.ungated], (canUseGridLayoutGate, gridLayoutModules) => canUseGridLayoutGate ? gridLayoutModules : {}));
export const _getSchemaGridLayoutTrees = createModuleListsSelector('schemaGridLayoutTrees');
export const getSchemaGridLayoutTrees = makeGatedSelector(getIsUngatedForImmerModuleReducer, createSelector([getCanUseGridLayoutGate, _getSchemaGridLayoutTrees.gated], (canUseGridLayoutGate, schemaGridLayoutTrees) => canUseGridLayoutGate ? schemaGridLayoutTrees : ImmutableMap()), createSelector([getCanUseGridLayoutGate, _getSchemaGridLayoutTrees.ungated], (canUseGridLayoutGate, schemaGridLayoutTrees) => canUseGridLayoutGate ? schemaGridLayoutTrees : {}));
const buildFakeModuleSliceStateForGridLayoutTrees = (schemaGridLayoutTrees, contentGridLayoutTrees, gridLayoutModules, auth, moduleSchemas) => ({
  modules: ImmutableMap({
    present: ImmutableMap({
      schemaGridLayoutTrees,
      gridLayoutTrees: contentGridLayoutTrees,
      gridLayoutModules
    })
  }),
  auth,
  moduleSchemas
});
const buildFakeModuleSliceStateForGridLayoutTreesImmer = (schemaGridLayoutTrees, contentGridLayoutTrees, gridLayoutModules, auth, moduleSchemas) => ({
  modules: {
    present: {
      schemaGridLayoutTrees,
      gridLayoutTrees: contentGridLayoutTrees,
      gridLayoutModules
    }
  },
  auth,
  moduleSchemas
});
const buildFakeModuleSliceStateForSpecificGridLayoutTree = (treeName, schemaGridLayoutTree, contentGridLayoutTree, gridLayoutModules, auth, moduleSchemas) => ({
  modules: ImmutableMap({
    present: ImmutableMap({
      schemaGridLayoutTrees: ImmutableMap({
        [treeName]: schemaGridLayoutTree
      }),
      gridLayoutTrees: ImmutableMap(contentGridLayoutTree ? {
        [treeName]: contentGridLayoutTree
      } : {}),
      gridLayoutModules
    })
  }),
  auth,
  moduleSchemas
});
const buildFakeModuleSliceStateForSpecificGridLayoutTreeImmer = (treeName, schemaGridLayoutTree, contentGridLayoutTree, gridLayoutModules, auth, moduleSchemas) => ({
  modules: {
    present: {
      schemaGridLayoutTrees: {
        [treeName]: schemaGridLayoutTree
      },
      gridLayoutTrees: contentGridLayoutTree ? {
        [treeName]: contentGridLayoutTree
      } : {},
      gridLayoutModules
    }
  },
  auth,
  moduleSchemas
});
const makeGetSpecificSchemaGridLayoutTree = treeName => createSelector(getSchemaGridLayoutTrees.gated, schemaTrees => schemaTrees.get(treeName));
const makeGetSpecificSchemaGridLayoutTreeImmer = treeName => createSelector(getSchemaGridLayoutTrees.ungated, schemaTrees => schemaTrees[treeName]);
const makeGetSpecificContentGridLayoutTree = treeName => createSelector(getContentGridLayoutTrees.gated, contentTrees => contentTrees.get(treeName));
const makeGetSpecificContentGridLayoutTreeImmer = treeName => createSelector(getContentGridLayoutTrees.ungated, contentTrees => contentTrees[treeName]);
const makeGetValueForNodeId = (id, treeName) => createSelector([makeGetSpecificContentGridLayoutTree(treeName), makeGetSpecificSchemaGridLayoutTree(treeName)], (contentTree, schemaTree) => {
  return contentTree ? contentTree.findNode(id).getValue() : schemaTree.findNode(id).getValue();
});
const makeGetValueForNodeIdImmer = (id, treeName) => createSelector([makeGetSpecificContentGridLayoutTreeImmer(treeName), makeGetSpecificSchemaGridLayoutTreeImmer(treeName)], (contentTree, schemaTree) => {
  return contentTree ? contentTree.findNode(id).getValue() : schemaTree.findNode(id).getValue();
});
export const getGridLayoutModulesMap = makeGatedSelector(getIsUngatedForImmerModuleReducer, createSelector(getGridLayoutModules.gated, gridLayoutModules => {
  return ImmutableMap(gridLayoutModules.valueSeq().flatMap(modulesForTree => {
    return modulesForTree.map(m => [m.get('name'), m]);
  }));
}), createSelector(getGridLayoutModules.ungated, gridLayoutModules => {
  return Object.fromEntries(Object.values(gridLayoutModules).flatMap(modulesForTree => modulesForTree.map(m => [m.name, m])));
}));
const makeGetGridLayoutModuleForImmer = moduleName => createSelector(getGridLayoutModulesMap.ungated, gridLayoutModulesMap => gridLayoutModulesMap[moduleName]);

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const makeGetGridLayoutModuleFor = moduleName => createSelector(getGridLayoutModulesMap.gated, gridLayoutModulesMap => {
  var _gridLayoutModulesMap;
  return (_gridLayoutModulesMap = gridLayoutModulesMap.get(moduleName)) === null || _gridLayoutModulesMap === void 0 ? void 0 : _gridLayoutModulesMap.toJS();
});
const makeGetContentTreeExistsForImmer = treeName => createSelector(getContentGridLayoutTrees.ungated, contentTrees => Object.hasOwn(contentTrees, treeName));

// We'll eventually use this
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const makeGetContentTreeExistsFor = treeName => createSelector(getContentGridLayoutTrees.gated, contentTrees => contentTrees.has(treeName));
export class MockImmutableGridLayoutModule extends createMockTypedImmutableMap('gridLayoutModule') {
  // No-op for now
  logUsage(__methodName) {}
}
const makeGetSpecificModuleInsideGridLayoutTree = (moduleName, treeName) => createSelector([makeGetValueForNodeId(moduleName, treeName), makeGetGridLayoutModuleFor(moduleName), makeGetContentTreeExistsFor(treeName), getAllModuleSchemasByModuleId], (nodeValue, gridLayoutModule, isFromContentTree, allModuleSchemasByModuleId) => {
  var _ref, _baseModule$body, _baseModule$label, _baseModule$module_id, _baseModule$module_id2, _moduleNodeValue$fiel, _moduleNodeValue$fiel2, _ref2, _baseModule$body2, _baseModule$name, _baseModule$name2;
  const moduleNodeValue = nodeValue;
  const baseModule = gridLayoutModule !== null && gridLayoutModule !== void 0 ? gridLayoutModule : moduleNodeValue;
  const moduleSpecId = gridLayoutModule ? gridLayoutModule.module_id : moduleNodeValue.moduleId;
  let moduleSpec;
  let fieldTypeNames = [];
  if (moduleSpecId) {
    moduleSpec = allModuleSchemasByModuleId[moduleSpecId];
    if (moduleSpec) {
      fieldTypeNames = getAllFieldNamesOnModuleSpec(moduleSpec);
    }
  }
  const contentModule = isFromContentTree ? moduleNodeValue : null;
  const defaultFieldOverrides = {
    path: {
      '@type': 'string',
      value: (_ref = (_baseModule$body = baseModule.body) === null || _baseModule$body === void 0 ? void 0 : _baseModule$body.path) !== null && _ref !== void 0 ? _ref : contentModule === null || contentModule === void 0 ? void 0 : contentModule.path
    }
  };
  let overrideable = baseModule.overrideable;
  // For now, default to true for buffered modules. We'll want to eventually
  // revise this once we have a more fleshed out plan for developer created
  // templates and modules for grids in general.
  if (overrideable === undefined && isFromContentTree) {
    overrideable = true;
  }

  // TODO: Should we create a grids version of attributesPickedFromContentWidgetImmer?
  let semiSerializedModuleThing = fromJS({
    type: 'module',
    grid_layout_id: treeName,
    jsModule: moduleNodeValue.jsModule,
    label: (_baseModule$label = baseModule.label) !== null && _baseModule$label !== void 0 ? _baseModule$label : moduleNodeValue.label,
    moduleId: (_baseModule$module_id = baseModule.module_id) !== null && _baseModule$module_id !== void 0 ? _baseModule$module_id : moduleNodeValue.moduleId,
    module_id: (_baseModule$module_id2 = baseModule.module_id) !== null && _baseModule$module_id2 !== void 0 ? _baseModule$module_id2 : moduleNodeValue.moduleId,
    fieldOverrides: (_moduleNodeValue$fiel = moduleNodeValue.fieldOverrides) !== null && _moduleNodeValue$fiel !== void 0 ? _moduleNodeValue$fiel : defaultFieldOverrides,
    body: (_moduleNodeValue$fiel2 = moduleNodeValue.fieldOverrides) !== null && _moduleNodeValue$fiel2 !== void 0 ? _moduleNodeValue$fiel2 : defaultFieldOverrides,
    path: (_ref2 = (_baseModule$body2 = baseModule.body) === null || _baseModule$body2 === void 0 ? void 0 : _baseModule$body2.path) !== null && _ref2 !== void 0 ? _ref2 : contentModule === null || contentModule === void 0 ? void 0 : contentModule.path,
    styles: moduleNodeValue.styles,
    id: (_baseModule$name = baseModule.name) !== null && _baseModule$name !== void 0 ? _baseModule$name : contentModule === null || contentModule === void 0 ? void 0 : contentModule.id,
    name: (_baseModule$name2 = baseModule.name) !== null && _baseModule$name2 !== void 0 ? _baseModule$name2 : contentModule === null || contentModule === void 0 ? void 0 : contentModule.id,
    overrideable,
    // TODO: Figure out smart content
    smart_objects: List([]),
    smart_type: 'NOT_SMART'
  });
  if (moduleNodeValue.fieldOverrides) {
    // Convert the field overrides from chirp avro to json
    let fieldOverrides = Object.entries(moduleNodeValue.fieldOverrides).reduce((acc, [key, value]) => {
      acc[key] = convertFieldValueAvroWithoutTypenameToJson(value);
      return acc;
    }, {});
    if (fieldTypeNames.length > 0) {
      // Filter out all schema attributes, except if a custom module field name matches one of those
      // schema attributes (like if a custom module has a `type` field)
      const attributesToFilter = ATTRIBUTES_PRIMARILY_FROM_CONTENT_WIDGET_WITH_EMPTY_SCHEMA_WIDGET.filter(attribute => !fieldTypeNames.includes(attribute));
      fieldOverrides = Object.entries(fieldOverrides).reduce((obj, [key, val]) => {
        if (!attributesToFilter.includes(key)) {
          obj[key] = val;
        }
        return obj;
      }, {});
      semiSerializedModuleThing = semiSerializedModuleThing.mergeIn(['fieldOverrides'], fieldOverrides);
      semiSerializedModuleThing = semiSerializedModuleThing.mergeIn(['body'], fieldOverrides);
    }
  }
  if (moduleNodeValue.styles && moduleNodeValue.styles.oldModuleStyles) {
    const oldModuleStyles = moduleNodeValue.styles.oldModuleStyles || {};
    const convertedCss = oldModuleStyles.css ? Object.entries(oldModuleStyles.css).reduce((acc, [key, value]) => {
      acc[key] = convertFieldValueAvroWithoutTypenameToJson(value);
      return acc;
    }, {}) : undefined;
    const convertedChildCss = oldModuleStyles.childCss ? Object.entries(oldModuleStyles.childCss).reduce((acc, [key, value]) => {
      acc[key] = convertFieldValueAvroWithoutTypenameToJson(value);
      return acc;
    }, {}) : undefined;
    semiSerializedModuleThing = semiSerializedModuleThing.mergeIn(['styles', 'oldModuleStyles', 'css'], convertedCss);
    semiSerializedModuleThing = semiSerializedModuleThing.mergeIn(['styles', 'oldModuleStyles', 'childCss'], convertedChildCss);
  }
  return semiSerializedModuleThing;
});
const makeGetSpecificModuleInsideGridLayoutTreeImmer = (moduleName, treeName) => createSelector([makeGetValueForNodeIdImmer(moduleName, treeName), makeGetGridLayoutModuleForImmer(moduleName), makeGetContentTreeExistsForImmer(treeName), getAllModuleSchemasByModuleId], (nodeValue, gridLayoutModule, isFromContentTree, allModuleSchemasByModuleId) => {
  var _ref3, _baseModule$body3, _baseModule$label2, _baseModule$module_id3, _baseModule$module_id4, _moduleNodeValue$fiel3, _moduleNodeValue$fiel4, _ref4, _baseModule$body4, _baseModule$name3, _baseModule$name4;
  const moduleNodeValue = nodeValue;
  const baseModule = gridLayoutModule !== null && gridLayoutModule !== void 0 ? gridLayoutModule : moduleNodeValue;
  const moduleSpecId = gridLayoutModule ? gridLayoutModule.module_id : moduleNodeValue.moduleId;
  let moduleSpec;
  let fieldTypeNames = [];
  if (moduleSpecId) {
    moduleSpec = allModuleSchemasByModuleId[moduleSpecId];
    if (moduleSpec) {
      fieldTypeNames = getAllFieldNamesOnModuleSpec(moduleSpec);
    }
  }
  const contentModule = isFromContentTree ? moduleNodeValue : null;
  const defaultFieldOverrides = {
    path: {
      '@type': 'string',
      value: (_ref3 = (_baseModule$body3 = baseModule.body) === null || _baseModule$body3 === void 0 ? void 0 : _baseModule$body3.path) !== null && _ref3 !== void 0 ? _ref3 : contentModule === null || contentModule === void 0 ? void 0 : contentModule.path
    }
  };
  let overrideable = baseModule.overrideable;
  // For now, default to true for buffered modules. We'll want to eventually
  // revise this once we have a more fleshed out plan for developer created
  // templates and modules for grids in general.
  if (overrideable === undefined && isFromContentTree) {
    overrideable = true;
  }

  // TODO: Should we create a grids version of attributesPickedFromContentWidgetImmer?
  let semiSerializedModuleThing = {
    type: 'module',
    grid_layout_id: treeName,
    jsModule: moduleNodeValue.jsModule,
    label: (_baseModule$label2 = baseModule.label) !== null && _baseModule$label2 !== void 0 ? _baseModule$label2 : moduleNodeValue.label,
    moduleId: (_baseModule$module_id3 = baseModule.module_id) !== null && _baseModule$module_id3 !== void 0 ? _baseModule$module_id3 : moduleNodeValue.moduleId,
    module_id: (_baseModule$module_id4 = baseModule.module_id) !== null && _baseModule$module_id4 !== void 0 ? _baseModule$module_id4 : moduleNodeValue.moduleId,
    fieldOverrides: (_moduleNodeValue$fiel3 = moduleNodeValue.fieldOverrides) !== null && _moduleNodeValue$fiel3 !== void 0 ? _moduleNodeValue$fiel3 : defaultFieldOverrides,
    body: (_moduleNodeValue$fiel4 = moduleNodeValue.fieldOverrides) !== null && _moduleNodeValue$fiel4 !== void 0 ? _moduleNodeValue$fiel4 : defaultFieldOverrides,
    path: (_ref4 = (_baseModule$body4 = baseModule.body) === null || _baseModule$body4 === void 0 ? void 0 : _baseModule$body4.path) !== null && _ref4 !== void 0 ? _ref4 : contentModule === null || contentModule === void 0 ? void 0 : contentModule.path,
    styles: moduleNodeValue.styles,
    id: (_baseModule$name3 = baseModule.name) !== null && _baseModule$name3 !== void 0 ? _baseModule$name3 : contentModule === null || contentModule === void 0 ? void 0 : contentModule.id,
    name: (_baseModule$name4 = baseModule.name) !== null && _baseModule$name4 !== void 0 ? _baseModule$name4 : contentModule === null || contentModule === void 0 ? void 0 : contentModule.id,
    overrideable,
    // TODO: Figure out smart content
    smart_objects: [],
    smart_type: 'NOT_SMART'
  };
  if (moduleNodeValue.fieldOverrides) {
    // Convert the field overrides from chirp avro to json
    let fieldOverrides = Object.entries(moduleNodeValue.fieldOverrides).reduce((acc, [key, value]) => {
      acc[key] = convertFieldValueAvroWithoutTypenameToJson(value);
      return acc;
    }, {});
    if (fieldTypeNames.length > 0) {
      // Filter out all schema attributes, except if a custom module field name matches one of those
      // schema attributes (like if a custom module has a `type` field)
      const attributesToFilter = ATTRIBUTES_PRIMARILY_FROM_CONTENT_WIDGET_WITH_EMPTY_SCHEMA_WIDGET.filter(attribute => !fieldTypeNames.includes(attribute));
      fieldOverrides = Object.entries(fieldOverrides).reduce((obj, [key, val]) => {
        if (!attributesToFilter.includes(key)) {
          obj[key] = val;
        }
        return obj;
      }, {});
      semiSerializedModuleThing = mergeIn(cloneDeep(semiSerializedModuleThing), ['fieldOverrides'], fieldOverrides);
      semiSerializedModuleThing = mergeIn(cloneDeep(semiSerializedModuleThing), ['body'], fieldOverrides);
    }
  }
  if (moduleNodeValue.styles && moduleNodeValue.styles.oldModuleStyles) {
    const oldModuleStyles = moduleNodeValue.styles.oldModuleStyles || {};
    const convertedCss = oldModuleStyles.css ? Object.entries(oldModuleStyles.css).reduce((acc, [key, value]) => {
      acc[key] = convertFieldValueAvroWithoutTypenameToJson(value);
      return acc;
    }, {}) : undefined;
    const convertedChildCss = oldModuleStyles.childCss ? Object.entries(oldModuleStyles.childCss).reduce((acc, [key, value]) => {
      acc[key] = convertFieldValueAvroWithoutTypenameToJson(value);
      return acc;
    }, {}) : undefined;
    semiSerializedModuleThing = mergeIn(cloneDeep(semiSerializedModuleThing), ['styles', 'oldModuleStyles', 'css'], convertedCss);
    semiSerializedModuleThing = mergeIn(cloneDeep(semiSerializedModuleThing), ['styles', 'oldModuleStyles', 'childCss'], convertedChildCss);
  }
  return new MockImmutableGridLayoutModule(semiSerializedModuleThing);
});
const makeGetCachedModulesInsideGridLayoutTree = treeName => {
  const cachedModuleSelectors = {};
  return createSelector(makeGetSpecificContentGridLayoutTree(treeName), makeGetSpecificSchemaGridLayoutTree(treeName), getGridLayoutModules.gated, getAuth, getModuleSchemas, (contentTree, schemaTree, gridLayoutModules, authState, moduleSchemas) => {
    const tree = contentTree || schemaTree;
    const fakedState = buildFakeModuleSliceStateForSpecificGridLayoutTree(treeName, schemaTree, contentTree, gridLayoutModules, authState, moduleSchemas);
    const moduleIds = Object.values(tree.getAllModulesById()).map(node => node.getId());
    return moduleIds.map(moduleName => {
      if (!cachedModuleSelectors[moduleName]) {
        cachedModuleSelectors[moduleName] = makeGetSpecificModuleInsideGridLayoutTree(moduleName, treeName);
      }
      return cachedModuleSelectors[moduleName](fakedState);
    });
  });
};
const makeGetCachedModulesInsideGridLayoutTreeImmer = treeName => {
  const cachedModuleSelectors = {};
  return createSelector(makeGetSpecificContentGridLayoutTreeImmer(treeName), makeGetSpecificSchemaGridLayoutTreeImmer(treeName), getGridLayoutModules.ungated, getAuth, getModuleSchemas, (contentTree, schemaTree, gridLayoutModules, authState, moduleSchemas) => {
    const tree = contentTree || schemaTree;
    const fakedState = buildFakeModuleSliceStateForSpecificGridLayoutTreeImmer(treeName, schemaTree, contentTree, gridLayoutModules, authState, moduleSchemas);
    const moduleIds = Object.values(tree.getAllModulesById()).map(node => node.getId());
    return moduleIds.map(moduleId => {
      if (!cachedModuleSelectors[moduleId]) {
        cachedModuleSelectors[moduleId] = makeGetSpecificModuleInsideGridLayoutTreeImmer(moduleId, treeName);
      }
      return cachedModuleSelectors[moduleId](fakedState);
    });
  });
};
export const makeCachedModulesInsideGridLayoutSelector = () => {
  const cachedModulesInTreeSelectors = {};
  return createSelector(getSchemaGridLayoutTrees.gated, getContentGridLayoutTrees.gated, getGridLayoutModules.gated, getAuth, getModuleSchemas, (schemaGridLayoutTrees, contentGridLayoutTrees, gridLayoutModules, auth, moduleSchemas) => {
    const modules = schemaGridLayoutTrees.keySeq().flatMap(treeName => {
      if (!cachedModulesInTreeSelectors[treeName]) {
        cachedModulesInTreeSelectors[treeName] = makeGetCachedModulesInsideGridLayoutTree(treeName);
      }
      const fakedState = buildFakeModuleSliceStateForGridLayoutTrees(schemaGridLayoutTrees, contentGridLayoutTrees, gridLayoutModules, auth, moduleSchemas);
      return cachedModulesInTreeSelectors[treeName](fakedState);
    });

    // Convert List to obj
    return Object.fromEntries(modules.map(m => [m.get('name'), m]).toArray());
  });
};
export const makeCachedModulesInsideGridLayoutSelectorImmer = () => {
  const cachedModulesInTreeSelectors = {};
  return createSelector(getSchemaGridLayoutTrees.ungated, getContentGridLayoutTrees.ungated, getGridLayoutModules.ungated, getAuth, getModuleSchemas, (schemaGridLayoutTrees, contentGridLayoutTrees, gridLayoutModules, auth, moduleSchemas) => {
    const modules = Object.keys(schemaGridLayoutTrees).flatMap(treeName => {
      if (!cachedModulesInTreeSelectors[treeName]) {
        cachedModulesInTreeSelectors[treeName] = makeGetCachedModulesInsideGridLayoutTreeImmer(treeName);
      }
      const fakedState = buildFakeModuleSliceStateForGridLayoutTreesImmer(schemaGridLayoutTrees, contentGridLayoutTrees, gridLayoutModules, auth, moduleSchemas);
      return cachedModulesInTreeSelectors[treeName](fakedState);
    });

    // Convert array to obj
    return Object.fromEntries(modules.map(m => [m.name, m]));
  });
};
const getAllTreeNamesImmer = createSelector(getSchemaGridLayoutTrees.ungated, schemaGridLayoutTree => Object.keys(schemaGridLayoutTree));
const getAllTreeNames = createSelector(getSchemaGridLayoutTrees.gated, schemaGridLayoutTree => schemaGridLayoutTree.keySeq().toArray());
class MockImmutableGridRootModule extends createMockTypedImmutableMap('fakeRootModule') {
  // No-op log usage for now
  logUsage(__methodName) {}
}
const makeGetSpecificSchemaTreeImmer = treeName => createSelector(getSchemaGridLayoutTrees.ungated, schemaTrees => schemaTrees[treeName]);
const makeGetSpecificSchemaTree = treeName => createSelector(getSchemaGridLayoutTrees.gated, schemaTrees => schemaTrees.get(treeName));
const makeGetSpecificContentTreeImmer = treeName => createSelector(getContentGridLayoutTrees.ungated, contentTrees => contentTrees[treeName]);
const makeGetSpecificContentTree = treeName => createSelector(getContentGridLayoutTrees.gated, contentTrees => contentTrees.get(treeName));
const makeGetTreeCellValueForImmer = (nodeName, treeName) => createSelector(makeGetSpecificSchemaTreeImmer(treeName), makeGetSpecificContentTreeImmer(treeName), (schemaTree, contentTree) => contentTree ? contentTree.findNode(nodeName).getValue() : schemaTree.findNode(nodeName).getValue());
const makeGetTreeCellValueFor = (nodeName, treeName) => createSelector(makeGetSpecificSchemaTree(treeName), makeGetSpecificContentTree(treeName), (schemaTree, contentTree) => contentTree ? contentTree.findNode(nodeName).getValue() : schemaTree.findNode(nodeName).getValue());
export const makeGetSchemaModuleForGridLayoutDndArea = treeName => createSelector(getSchemaWidgets.gated, schemaWidgets => schemaWidgets.get(treeName));
export const makeGetSchemaModuleForGridLayoutDndAreaImmer = treeName => createSelector(getSchemaWidgets.ungated, schemaWidgets => schemaWidgets[treeName]);
const makeGetRootModuleForGridLayoutImmer = treeName => createSelector(makeGetSchemaModuleForGridLayoutDndAreaImmer(treeName), makeGetSpecificSchemaTreeImmer(treeName), makeGetTreeCellValueForImmer(treeName, treeName), (schemaModuleForGridLayoutDndArea, schemaTree, treeRootValue) => {
  const schemaModule = schemaModuleForGridLayoutDndArea !== null && schemaModuleForGridLayoutDndArea !== void 0 ? schemaModuleForGridLayoutDndArea : schemaTree.getRootNode().getValue();
  return new MockImmutableGridRootModule({
    type: 'grid_layout',
    name: schemaModule.name || treeName,
    label: schemaModule.label || treeRootValue.label,
    order: schemaModule.order,
    // TODO: Figure out why this is required?
    line_number: null
  });
});
const makeGetRootModuleForGridLayout = treeName => createSelector(makeGetSchemaModuleForGridLayoutDndArea(treeName), makeGetSpecificSchemaTree(treeName), makeGetTreeCellValueFor(treeName, treeName), (schemaModuleForGridLayoutDndArea, schemaTree, treeRootValue) => {
  const schemaModule = schemaModuleForGridLayoutDndArea !== null && schemaModuleForGridLayoutDndArea !== void 0 ? schemaModuleForGridLayoutDndArea : schemaTree.getRootNode().getValue();
  // @ts-ignore not typing this immutable usage
  return new ImmutableMap({
    type: 'grid_layout',
    name: schemaModule.get('name') || treeName,
    label: schemaModule.get('label') || treeRootValue.label,
    order: schemaModule.get('order'),
    line_number: null
  });
});
const buildFakeModuleSliceStateForTrees = (schemaGridLayoutTrees, contentGridLayoutTrees, gridLayoutModules, auth, moduleSchemas, schemaWidgets) => ({
  modules: ImmutableMap({
    present: ImmutableMap({
      schemaGridLayoutTrees,
      gridLayoutTrees: contentGridLayoutTrees,
      gridLayoutModules,
      schemaWidgets
    })
  }),
  auth,
  moduleSchemas
});
const buildFakeModuleSliceStateForTreesImmer = (schemaGridLayoutTrees, contentGridLayoutTrees, gridLayoutModules, auth, moduleSchemas, schemaWidgets) => ({
  modules: {
    present: {
      schemaGridLayoutTrees,
      gridLayoutTrees: contentGridLayoutTrees,
      gridLayoutModules,
      schemaWidgets
    }
  },
  auth,
  moduleSchemas
});

// TODO: Cache this one downstream inside of moduleSelectors
export const makeCachedRootModulesForEachGridLayoutSelectorImmer = () => {
  const rootModuleSelectorCache = {};
  return createSelector(getAllTreeNamesImmer, getSchemaGridLayoutTrees.ungated, getContentGridLayoutTrees.ungated, getGridLayoutModules.ungated, getAuth, getModuleSchemas, getSchemaWidgets.ungated, (allTreeNames, schemaGridLayoutTrees, contentGridLayoutTrees, gridLayoutModules, auth, moduleSchemas, schemaWidgets) => {
    const fakeState = buildFakeModuleSliceStateForTreesImmer(schemaGridLayoutTrees, contentGridLayoutTrees, gridLayoutModules, auth, moduleSchemas, schemaWidgets);
    return allTreeNames.map(treeName => {
      if (!rootModuleSelectorCache[treeName]) {
        rootModuleSelectorCache[treeName] = makeGetRootModuleForGridLayoutImmer(treeName);
      }
      return rootModuleSelectorCache[treeName](fakeState);
    });
  });
};
export const makeCachedRootModulesForEachGridLayoutSelector = () => {
  const rootModuleSelectorCache = {};
  return createSelector(getAllTreeNames, getSchemaGridLayoutTrees.gated, getContentGridLayoutTrees.gated, getGridLayoutModules.gated, getAuth, getModuleSchemas, getSchemaWidgets.gated, (allTreeNames, schemaGridLayoutTree, contentGridLayoutTrees, gridLayoutModules, auth, moduleSchemas, schemaWidgets) => {
    const fakeState = buildFakeModuleSliceStateForTrees(schemaGridLayoutTree, contentGridLayoutTrees, gridLayoutModules, auth, moduleSchemas, schemaWidgets);
    // @ts-ignore This was the way that the immutable was already used
    return new List(allTreeNames).map(treeName => {
      if (!rootModuleSelectorCache[treeName]) {
        rootModuleSelectorCache[treeName] = makeGetRootModuleForGridLayout(treeName);
      }
      return rootModuleSelectorCache[treeName](fakeState);
    });
  });
};